import React from "react";

const MatchProfileSkeleton = ({ isDarkMode = false }) => {
  // Skeleton animation class
  const skeletonClass = `animate-pulse bg-gray-200 dark:darktitleColor rounded`;

  // Generate multiple skeleton market items
  const generateSkeletonMarkets = (count = 5) => {
    return Array.from({ length: count }, (_, index) => (
      <div key={index} className="mb-2">
        {/* Market Header Skeleton */}
        <div className="w-full flex justify-between items-center max-sm:p-[10px_15px] p-[10px_23px] bg-bglightblue dark:bg-bluedarkmode">
          <div className={`${skeletonClass} h-4 w-32`}></div>
          <div className="flex items-center space-x-2">
            <div className={`${skeletonClass} h-3 w-16`}></div>
            <div className={`${skeletonClass} h-4 w-4`}></div>
          </div>
        </div>

        {/* Market Odds Grid Skeleton */}
        <div className="grid grid-cols-3 gap-2 p-3">
          {Array.from({ length: 3 }, (_, oddIndex) => (
            <div
              key={oddIndex}
              className="rounded-md border bg-[#F3F4F6] dark:bg-darktitleColor border-[#E5E7EB] dark:border-doubleextralightwhite"
            >
              <div className="p-3 text-center space-y-1">
                <div className={`${skeletonClass} h-3 w-full mb-2`}></div>
                <div className={`${skeletonClass} h-4 w-12 mx-auto`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <div className="max-sm:overflow-hidden max-sm:mt-[5px] mt-3 s18:mt-[17px] rounded-[7px] bg-white dark:bg-bluedarkmodecolor shadow-dashboardshadow relative">
      {/* Team Header Skeleton */}
      <div className="flex justify-between items-center mt-4 px-4 py-6">
        <div className="text-center flex-1">
          <div className={`${skeletonClass} h-6 w-24 mx-auto mb-2`}></div>
          <div className={`${skeletonClass} h-4 w-16 mx-auto`}></div>
        </div>
        <div className="text-xl font-bold mx-4 text-darkblue dark:text-white">
          <div className={`${skeletonClass} h-6 w-8`}></div>
        </div>
        <div className="text-center flex-1">
          <div className={`${skeletonClass} h-6 w-24 mx-auto mb-2`}></div>
          <div className={`${skeletonClass} h-4 w-16 mx-auto`}></div>
        </div>
      </div>

      {/* Match Info Skeleton */}
      <div className="px-4 py-2 text-center">
        <div className={`${skeletonClass} h-4 w-40 mx-auto mb-2`}></div>
        <div className={`${skeletonClass} h-3 w-32 mx-auto`}></div>
      </div>

      {/* Market Categories Navigation Skeleton */}
      <div className="flex overflow-x-auto scrollbar-hide border-b border-gray-200 dark:border-gray-600 px-4">
        {Array.from({ length: 7 }, (_, index) => (
          <div
            key={index}
            className="flex items-center space-x-1 px-4 py-3 whitespace-nowrap min-w-fit"
          >
            <div className={`${skeletonClass} h-4 w-4 rounded-full`}></div>
            <div className={`${skeletonClass} h-4 w-16`}></div>
          </div>
        ))}
      </div>

      {/* Search Bar Skeleton */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-600">
        <div className="relative">
          <div className={`${skeletonClass} h-10 w-full rounded-lg`}></div>
        </div>
      </div>

      {/* Markets and Odds Skeleton */}
      <div className="bg-white dark:bg-bluedarkmodecolor shadow-dashboardshadow sm:!rounded-[0_0_12px_12px]">
        {generateSkeletonMarkets(6)}
      </div>

      {/* Loading overlay with pulsing effect */}
      <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 flex items-center justify-center rounded-[7px]">
        <div className="flex flex-col items-center space-y-3">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-blue-200 dark:border-blue-800 rounded-full animate-spin border-t-blue-500 dark:border-t-blue-400"></div>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
            Loading match data...
          </div>
        </div>
      </div>
    </div>
  );
};

// Alternative version without overlay - just skeleton content
const MatchProfileSkeletonClean = ({ isDarkMode = false }) => {
  const skeletonClass = `animate-pulse bg-gray-200 dark:darktitleColor rounded`;

  const generateSkeletonMarkets = (count = 6) => {
    return Array.from({ length: count }, (_, index) => (
      <div key={index} className="mb-2">
        {/* Market Header Skeleton */}
        <div className="w-full flex justify-between items-center max-sm:p-[10px_15px] p-[10px_23px] bg-bglightblue dark:bg-bluedarkmode">
          <div className={`${skeletonClass} h-4 w-32`}></div>
          <div className="flex items-center space-x-2">
            <div className={`${skeletonClass} h-3 w-16`}></div>
            <div className={`${skeletonClass} h-4 w-4`}></div>
          </div>
        </div>

        {/* Expanded market odds skeleton (for first few markets) */}
        {index < 3 && (
          <div className="grid grid-cols-3 gap-2 p-3">
            {Array.from(
              { length: Math.floor(Math.random() * 4) + 2 },
              (_, oddIndex) => (
                <div
                  key={oddIndex}
                  className="rounded-md border bg-[#F3F4F6] dark:bg-[#2F425C] border-[#E5E7EB] dark:border-doubleextralightwhite"
                >
                  <div className="p-3 text-center space-y-2">
                    <div className={`${skeletonClass} h-3 w-full`}></div>
                    <div className={`${skeletonClass} h-4 w-12 mx-auto`}></div>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="max-sm:overflow-hidden max-sm:mt-[5px] mt-3 s18:mt-[17px] rounded-[7px] bg-white dark:bg-bluedarkmodecolor shadow-dashboardshadow relative">
      {/* Team Header Skeleton */}
      <div className="flex justify-between items-center mt-4 px-4 py-6">
        <div className="text-center flex-1">
          <div className={`${skeletonClass} h-6 w-28 mx-auto mb-2`}></div>
        </div>
        <div className="text-xl font-bold mx-4">
          <div className={`${skeletonClass} h-6 w-8`}></div>
        </div>
        <div className="text-center flex-1">
          <div className={`${skeletonClass} h-6 w-28 mx-auto mb-2`}></div>
        </div>
      </div>

      {/* Match Info Skeleton */}
      <div className="px-4 py-2 text-center border-b border-gray-200 dark:border-gray-600">
        <div className={`${skeletonClass} h-4 w-48 mx-auto mb-2`}></div>
        <div className={`${skeletonClass} h-3 w-32 mx-auto`}></div>
      </div>

      {/* Market Categories Navigation Skeleton */}
      <div className="flex overflow-x-auto scrollbar-hide border-b border-gray-200 dark:border-gray-600">
        {Array.from({ length: 7 }, (_, index) => (
          <div
            key={index}
            className={`flex items-center space-x-2 px-4 py-3 whitespace-nowrap min-w-fit border-b-2 ${
              index === 0
                ? "border-blue-500 dark:border-blue-400"
                : "border-transparent"
            }`}
          >
            {index === 0 && (
              <div className={`${skeletonClass} h-4 w-4 rounded-full`}></div>
            )}
            <div
              className={`${skeletonClass} h-4 ${
                index === 0 ? "w-16" : "w-12"
              }`}
            ></div>
          </div>
        ))}
      </div>

      {/* Markets and Odds Skeleton */}
      <div className="bg-white dark:bg-bluedarkmodecolor shadow-dashboardshadow sm:!rounded-[0_0_12px_12px]">
        {generateSkeletonMarkets(8)}
      </div>
    </div>
  );
};

// Compact skeleton version for mobile or smaller spaces
const MatchProfileSkeletonCompact = ({ isDarkMode = false }) => {
  const skeletonClass = `animate-pulse bg-gray-200 dark:bg-gray-700 rounded`;

  return (
    <div className="max-sm:overflow-hidden max-sm:mt-[5px] mt-3 s18:mt-[17px] rounded-[7px] bg-white dark:bg-bluedarkmodecolor shadow-dashboardshadow relative">
      {/* Compact Team Header */}
      <div className="flex items-center justify-center space-x-4 p-4">
        <div className={`${skeletonClass} h-5 w-20`}></div>
        <div className={`${skeletonClass} h-4 w-6`}></div>
        <div className={`${skeletonClass} h-5 w-20`}></div>
      </div>

      {/* Compact Categories */}
      <div className="flex space-x-2 px-4 pb-4 overflow-x-auto">
        {Array.from({ length: 5 }, (_, i) => (
          <div
            key={i}
            className={`${skeletonClass} h-8 w-16 flex-shrink-0`}
          ></div>
        ))}
      </div>

      {/* Compact Markets */}
      <div className="space-y-3 p-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="space-y-2">
            <div className={`${skeletonClass} h-4 w-24`}></div>
            <div className="grid grid-cols-2 gap-2">
              <div className={`${skeletonClass} h-12`}></div>
              <div className={`${skeletonClass} h-12`}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MatchProfileSkeleton;
export { MatchProfileSkeletonClean, MatchProfileSkeletonCompact };
